/**
 * Cornerstone v2 preview — Parent Dashboard.
 *
 * This is a redesigned visual layer on top of the EXISTING /parent/overview API.
 * No new backend, no business logic changes. It is mounted at /preview/parent
 * so it does not touch the live /parent page until sign-off.
 *
 * Design follows the Yamaha-serene direction:
 *   • White background, dark-purple hero card, light-blue accents
 *   • Rounded 2xl cards, generous spacing, mobile-first
 *   • Fixed bottom navigation (5 tabs) on mobile
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Bell, Calendar, MessageSquare, CalendarCheck2, CreditCard, FileText,
  ChevronRight, AlertCircle, User, Megaphone,
} from "lucide-react";
import { useList, fmtRM, dayName } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { useV2Theme, BottomNav } from "./_shell.jsx";

export default function ParentDashboardV2Preview() {
  useV2Theme();
  const { data, loading } = useList("/parent/overview");
  const { user } = useAuth() || {};
  const nextLesson = useMemo(() => {
    const list = data?.schedules || [];
    return list[0] || null;
  }, [data]);

  if (loading || !data) return <div className="min-h-screen bg-white flex items-center justify-center"><Spinner /></div>;

  const parentName = (data.parent?.full_name || "Parent").split(" ")[0];
  const unpaid = data.unpaid_invoices || [];
  const firstUnpaid = unpaid[0];
  const outstanding = data.outstanding_total || 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-white text-[color:var(--v2-text)] pb-24" data-testid="preview-parent-v2">
      <div className="mx-auto max-w-md sm:max-w-2xl lg:max-w-4xl px-5 pt-5">

        {/* ── header ─────────────────────────────────────────────── */}
        <header className="flex items-center justify-between mb-6" data-testid="v2-header">
          <div>
            <p className="text-[13px] text-[color:var(--v2-text-muted)]">Good day 👋</p>
            <h1 className="font-display text-2xl font-semibold tracking-tight">
              Hi, {parentName}&apos;s Parent
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-2 rounded-full hover:bg-[color:var(--v2-soft-grey)] transition-colors" data-testid="v2-bell">
              <Bell className="h-5 w-5 text-[color:var(--v2-text)]" />
              {data.unread_notifications > 0 && (
                <span className="absolute -top-0 -right-0 h-4 min-w-[16px] px-1 rounded-full bg-[color:var(--v2-danger)] text-white text-[10px] font-semibold flex items-center justify-center">
                  {data.unread_notifications}
                </span>
              )}
            </button>
            <div className="h-10 w-10 rounded-full bg-[color:var(--v2-light-blue)] flex items-center justify-center border border-[color:var(--v2-border)]" data-testid="v2-avatar">
              <User className="h-5 w-5 text-[color:var(--v2-primary)]" />
            </div>
          </div>
        </header>

        {/* ── next lesson hero (dark purple card) ────────────────── */}
        <section className="relative overflow-hidden rounded-2xl p-5 mb-5"
          style={{ background: "linear-gradient(135deg, #3E1C5E 0%, #5B2A86 100%)" }}
          data-testid="v2-next-lesson">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center">
              <CalendarCheck2 className="h-4 w-4 text-white" />
            </div>
            <span className="text-white/80 text-sm font-medium tracking-wide">Next Lesson</span>
          </div>
          {nextLesson ? (
            <>
              <p className="text-white text-xl font-display font-semibold mb-1">
                {nextLesson.course_name}
              </p>
              <p className="text-white/80 text-sm mb-1">
                {dayName(nextLesson.day_of_week)} · {nextLesson.start_time}–{nextLesson.end_time}
              </p>
              <p className="text-white/70 text-sm mb-4">
                Teacher: {nextLesson.teacher_name} · {nextLesson.studio_name}
              </p>
              <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--v2-accent-blue)]/20 text-[color:var(--v2-accent-blue)] px-3 py-1 text-xs font-semibold border border-[color:var(--v2-accent-blue)]/30">
                Confirmed
              </span>
              <Link to="/parent/timetable" className="mt-5 flex items-center justify-between text-white/95 border-t border-white/10 pt-4"
                data-testid="v2-view-schedule">
                <span className="text-sm font-medium">View full schedule</span>
                <ChevronRight className="h-4 w-4" />
              </Link>
            </>
          ) : (
            <p className="text-white/85 text-sm">No lessons scheduled yet.</p>
          )}
        </section>

        {/* ── important alerts ───────────────────────────────────── */}
        {firstUnpaid && (
          <section className="mb-5" data-testid="v2-alerts">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-[15px] font-semibold">Important Alerts</h2>
              <Link to="/parent/invoices" className="text-xs text-[color:var(--v2-primary)] font-medium">View all</Link>
            </div>
            <div className="rounded-xl border border-[color:var(--v2-warning-bg)] bg-[color:var(--v2-warning-bg)] p-4 flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-[color:var(--v2-warning)] flex items-center justify-center shrink-0">
                <AlertCircle className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-[color:var(--v2-text)]">Payment Due</p>
                <p className="text-xs text-[color:var(--v2-text-muted)] mt-0.5">
                  Invoice {firstUnpaid.invoice_number} is due on {firstUnpaid.due_date}.
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-[color:var(--v2-text-muted)] mt-1" />
            </div>
          </section>
        )}

        {/* ── outstanding balance ─────────────────────────────────── */}
        <section className="mb-5" data-testid="v2-outstanding">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[15px] font-semibold">Outstanding Balance</h2>
            <Link to="/parent/invoices" className="text-xs text-[color:var(--v2-primary)] font-medium">View all</Link>
          </div>
          {unpaid.length === 0 ? (
            <div className="rounded-xl border border-[color:var(--v2-border)] bg-[color:var(--v2-soft-grey)] p-5 text-center">
              <p className="text-sm text-[color:var(--v2-text-muted)]">🎉 All paid up. Thank you!</p>
            </div>
          ) : (
            <div className="rounded-xl border border-[color:var(--v2-border)] bg-white shadow-[0_1px_2px_rgba(20,33,61,0.04)] p-4 flex items-center justify-between">
              <div>
                <p className="font-mono text-xs text-[color:var(--v2-text-muted)]">
                  {firstUnpaid.invoice_number}
                </p>
                <p className="text-[11px] text-[color:var(--v2-text-muted)]">Due {firstUnpaid.due_date}</p>
              </div>
              <div className="text-right">
                <p className="text-[color:var(--v2-danger)] font-bold text-lg leading-none">{fmtRM(outstanding)}</p>
                <Link to="/parent/invoices"
                  className="mt-2 inline-flex items-center gap-1 rounded-full bg-[color:var(--v2-primary)] hover:bg-[color:var(--v2-primary-dark)] transition-transform transition-shadow duration-200 hover:-translate-y-0.5 text-white text-xs font-semibold px-4 py-1.5"
                  data-testid="v2-pay-now-btn">
                  Pay Now
                </Link>
              </div>
            </div>
          )}
        </section>

        {/* ── quick actions grid ─────────────────────────────────── */}
        <section className="mb-5" data-testid="v2-quick-actions">
          <h2 className="text-[15px] font-semibold mb-2">Quick Actions</h2>
          <div className="grid grid-cols-4 gap-3">
            <QuickAction to="/parent/timetable" icon={Calendar} label="Schedule" />
            <QuickAction to="/parent/invoices" icon={CreditCard} label="Pay" />
            <QuickAction to="/parent/leaves" icon={FileText} label="Leave" />
            <QuickAction to="/parent/notifications" icon={MessageSquare} label="Messages" />
          </div>
        </section>

        {/* ── latest announcement ────────────────────────────────── */}
        <section className="mb-6" data-testid="v2-announcement">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[15px] font-semibold">Latest Announcement</h2>
          </div>
          <div className="rounded-xl bg-[color:var(--v2-light-blue)] border border-[color:var(--v2-accent-blue)]/30 p-4 flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-[color:var(--v2-primary)] flex items-center justify-center shrink-0">
              <Megaphone className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm text-[color:var(--v2-text)]">Welcome to the new Cornerstone experience</p>
              <p className="text-xs text-[color:var(--v2-text-muted)] mt-0.5">
                We&apos;re rolling out a refreshed look to make your family&apos;s music journey clearer, calmer and faster to navigate. Same features, smoother experience.
              </p>
            </div>
          </div>
        </section>

        <p className="text-center text-xs text-[color:var(--v2-text-muted)] mb-4">
          v2 preview · <button onClick={() => { localStorage.removeItem("cornerstone-theme"); window.location.href = "/parent"; }} className="underline">back to current design</button>
        </p>
      </div>

      {/* ── bottom nav (mobile-first, visible on all sizes for preview parity) */}
      <BottomNav />
    </div>
  );
}

function QuickAction({ to, icon: Icon, label }) {
  return (
    <Link to={to}
      className="flex flex-col items-center gap-1.5 rounded-xl bg-[color:var(--v2-soft-grey)] hover:bg-[color:var(--v2-light-blue)] border border-[color:var(--v2-border)] py-3 transition-colors"
      data-testid={`v2-qa-${label.toLowerCase()}`}>
      <Icon className="h-5 w-5 text-[color:var(--v2-primary)]" />
      <span className="text-[11px] font-medium text-[color:var(--v2-text)]">{label}</span>
    </Link>
  );
}
